import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Avatar, Button, Divider } from 'antd';
import {
  CrownOutlined,
  DownOutlined,
  EditOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

const PLAN_LABEL: Record<string, string> = {
  free: '免费版',
  basic: '基础版',
  pro: '专业版',
  enterprise: '企业版',
};

const PLAN_COLOR: Record<string, string> = {
  free: '#8C8C8C',
  basic: '#1677FF',
  pro: '#F6AD55',
  enterprise: '#2453D4',
};

const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { currentTenant } = useTenant();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  if (!user) return null;

  const displayName = user.real_name || user.username || user.email.split('@')[0];
  const plan = currentTenant?.plan || 'free';

  const handleTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (triggerRef.current?.contains(event.target as Node) || panelRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/login');
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 10,
    cursor: 'pointer',
    color: '#262626',
    fontSize: 14,
    userSelect: 'none',
  };

  const panel = open ? createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: dropPos.top,
        right: dropPos.right,
        width: 280,
        background: '#FFFFFF',
        borderRadius: 16,
        boxShadow: '0 20px 48px rgba(10,25,47,0.16)',
        padding: '16px 12px 12px',
        zIndex: 99999,
        border: '1px solid #E7EDF4',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12 }}>
        <Avatar
          size={44}
          icon={<UserOutlined />}
          src={user.avatar_url}
          style={{ background: '#1677FF', flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#262626', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </div>
          <div style={{ fontSize: 12, color: '#8C8C8C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email}
          </div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 6,
              fontSize: 11,
              color: PLAN_COLOR[plan] || '#8C8C8C',
              background: `${PLAN_COLOR[plan] || '#8C8C8C'}14`,
              padding: '2px 9px',
              borderRadius: 999,
              border: `1px solid ${PLAN_COLOR[plan] || '#8C8C8C'}2E`,
            }}
          >
            <CrownOutlined />
            {PLAN_LABEL[plan] || plan}
          </span>
        </div>
      </div>

      <Divider style={{ margin: '0 0 6px' }} />

      {[
        { icon: <UserOutlined style={{ color: '#1677FF' }} />, label: '个人账户', path: '/personal-account' },
        { icon: <CrownOutlined style={{ color: '#F6AD55' }} />, label: '套餐管理', path: '/subscription' },
        { icon: <EditOutlined style={{ color: '#1677FF' }} />, label: '修改信息 / 密码', path: '/settings/account' },
        { icon: <SettingOutlined style={{ color: '#1677FF' }} />, label: '机构设置', path: '/settings/tenant' },
      ].map((item) => (
        <div
          key={item.path}
          style={itemStyle}
          onMouseEnter={(event) => { event.currentTarget.style.background = '#F4F8FC'; }}
          onMouseLeave={(event) => { event.currentTarget.style.background = 'transparent'; }}
          onClick={() => go(item.path)}
        >
          {item.icon}
          <span>{item.label}</span>
        </div>
      ))}

      <Divider style={{ margin: '6px 0' }} />

      <Button
        type="text"
        danger
        block
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        style={{ textAlign: 'left', height: 40, borderRadius: 10, fontWeight: 500 }}
      >
        退出登录
      </Button>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleTriggerClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          cursor: 'pointer',
          height: 40,
          padding: '0 10px 0 8px',
          borderRadius: 12,
          userSelect: 'none',
          background: '#F8FAFD',
          border: '1px solid #E7EDF4',
        }}
        onMouseEnter={(event) => { event.currentTarget.style.background = '#F1F6FB'; }}
        onMouseLeave={(event) => { event.currentTarget.style.background = '#F8FAFD'; }}
      >
        <Avatar size="small" icon={<UserOutlined />} src={user.avatar_url} style={{ background: '#1677FF' }} />
        <div style={{ maxWidth: 144, minWidth: 0 }}>
          <div style={{ color: '#262626', fontSize: 13, fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </div>
        </div>
        <DownOutlined style={{ fontSize: 11, color: '#8C8C8C' }} />
      </div>
      {panel}
    </>
  );
};

export default UserMenu;
